import type { AuthRoutingResources } from "@features/auth/AuthUserContext";
import type { AuthTokenState } from "@features/auth/useAuthToken";
import type { PermissionSet } from "@features/authz/can";
import type { AuthZOrganization } from "@features/authz/orgPolicy";
import type { PermissionId } from "@features/authz/permissionIds";
import {
  type ActivePluginSet,
  pluginSetMatchesOrganization,
} from "@features/plugins/orgPlugins";
import type { UserRole } from "@models/UserRole";

export type RouterTarget =
  | { kind: "public" }
  | {
      kind: "authenticated";
      organization: AuthZOrganization;
      activePluginSet: ActivePluginSet;
      role: UserRole;
      permissions: PermissionSet;
    };

export type RouterTargetResolution =
  | { status: "pending" }
  | { status: "error"; error: unknown }
  | { status: "resolved"; target: RouterTarget };

type RouterTargetInputs = AuthRoutingResources & {
  session: AuthTokenState;
  /**
   * Resolved by `AuthZProvider`, which is the single owner of policy compile.
   * Taken as input rather than compiled here so UI and routes cannot disagree.
   */
  permissions: PermissionSet;
};

export function resolveRouterTarget({
  session,
  currentUser,
  userError,
  activePluginsQuery,
  activePluginSet,
  permissions,
}: RouterTargetInputs): RouterTargetResolution {
  switch (session.status) {
    case "loading":
      return { status: "pending" };
    case "auth0Loading":
    case "signedOut":
    case "error":
      return { status: "resolved", target: { kind: "public" } };
    case "ready":
      break;
    default: {
      const unreachable: never = session;
      return unreachable;
    }
  }

  if (!currentUser) {
    if (userError !== null && userError !== undefined) {
      return { status: "error", error: userError };
    }
    return { status: "pending" };
  }

  if (
    activePluginSet &&
    pluginSetMatchesOrganization(activePluginSet, currentUser.organization)
  ) {
    return {
      status: "resolved",
      target: {
        kind: "authenticated",
        organization: currentUser.organization,
        activePluginSet,
        role: currentUser.role,
        permissions,
      },
    };
  }

  if (activePluginsQuery.status === "error") {
    return { status: "error", error: activePluginsQuery.error };
  }

  return { status: "pending" };
}

/**
 * Router remount segment for this principal's granted permissions.
 *
 * Hashed rather than joined because the ID catalog is expected to reach hundreds:
 * a joined key would scale with permissions *granted* (kilobytes) where the old
 * override-based key scaled with overrides *configured*. Folding incrementally
 * over sorted IDs keeps that string from ever being materialized.
 *
 * A 32-bit hash over a closed, committed ID list is an acceptable collision risk:
 * the cost of a collision is one skipped router remount on an admin settings
 * change, not an access-control failure — `can()` is unaffected.
 */
function permissionsRouterKey(permissions: PermissionSet): string {
  const ids = [...permissions].sort() as PermissionId[];
  if (ids.length === 0) return "empty";

  // FNV-1a, 32-bit.
  let hash = 0x811c9dc5;
  for (const id of ids) {
    for (let i = 0; i < id.length; i += 1) {
      hash ^= id.charCodeAt(i);
      hash = Math.imul(hash, 0x01000193);
    }
    hash ^= 0x7c; // "|" separator, so ["ab","c"] and ["a","bc"] differ
    hash = Math.imul(hash, 0x01000193);
  }

  return `${ids.length}-${(hash >>> 0).toString(16)}`;
}

export function routerTargetKey(target: RouterTarget): string {
  switch (target.kind) {
    case "public":
      return "public";
    case "authenticated":
      return `authenticated:${target.activePluginSet.revision}:${target.role}:${permissionsRouterKey(target.permissions)}`;
    default: {
      const unreachable: never = target;
      return unreachable;
    }
  }
}
